<template>
  <el-dialog
      :title="title"
      :visible.sync="dialogShow"
      width="900px"
      top="8vh"
      append-to-body
      class="literature-search-dialog"
      @close="handleDialogClose"
  >
    <!-- 搜索区域 -->
    <div class="search-container">
      <div class="search-box">
        <i class="el-icon-search search-icon"></i>
        <el-input
            v-model="searchKey"
            :placeholder="placeholder"
            @input="onInput"
            clearable
            :disabled="tableLoading"
            class="search-input"
        />
        <div v-if="searchKey && !tableLoading" class="search-result-count">
          共 <span class="count-num">{{ total }}</span> 条结果
        </div>
      </div>

      <!-- 分类筛选（树形结构，支持选择任意层级节点） -->
      <div class="category-filter">
        <span class="filter-label">分类筛选：</span>
        <el-cascader
            v-model="selectedDepartmentIds"
            :options="departmentTree"
            :props="{ multiple: false, expandTrigger: 'hover', checkStrictly: true }"
            :placeholder="type === 'case' ? '选择病例分类' : '选择文献分类'"
            :disabled="tableLoading"
            class="category-cascader"
            clearable
            @change="onCategoryChange"
        />
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-container">
      <!-- 空状态 -->
      <div v-if="!searchKey && selectedDepartmentIds.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="el-icon-search"></i>
        </div>
        <p class="empty-title">{{ title }}</p>
        <p class="empty-desc">{{ emptyDescription }}</p>
      </div>

      <!-- 搜索结果列表 -->
      <div v-else class="result-list">
        <div
            v-for="(item, index) in literatureList"
            :key="index"
            class="result-item"
            @click="handleSelect(item)"
        >
          <div class="item-icon">
            <i class="el-icon-file-text"></i>
          </div>
          <div class="item-content">
            <h4 class="item-title">{{ item.liteName }}</h4>
            <p class="item-meta">
              <span class="meta-item">
                <i class="el-icon-user"></i>
                {{ item.liteAuthor || '未知作者' }}
              </span>
              <span class="meta-divider">·</span>
              <span class="meta-item">
                <i class="el-icon-calendar"></i>
                {{ formatDate(item.createTime) }}
              </span>
            </p>
          </div>
          <div class="item-action">
            <el-button size="small" type="primary" class="select-btn">选择</el-button>
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="tableLoading" class="loading-state">
          <el-spin tip="搜索中..." size="large"/>
        </div>

        <!-- 无结果状态 -->
        <div v-else-if="!tableLoading && literatureList.length === 0" class="empty-state">
          <div class="empty-icon no-result">
            <i class="el-icon-folder-opened"></i>
          </div>
          <p class="empty-title">未找到匹配内容</p>
          <p class="empty-desc">请尝试其他关键词</p>
        </div>
      </div>
    </div>

    <!-- 分页区域 -->
    <div v-if="(searchKey || selectedDepartmentIds.length > 0) && total > pageSize" slot="footer" class="pagination-footer">
      <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          background
      />
    </div>
    <div v-else slot="footer" class="footer-actions">
      <el-button @click="dialogShow = false" class="cancel-btn">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getLiterature, getDepartmentList } from '@/api/aiassistant'
export default {
  name: 'LiteratureSearchPopup',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'literature'  // literature=文献解读，case=案例解读
    }
  },
  computed: {
    title() {
      return this.type === 'case' ? '病例搜索' : '文献搜索'
    },
    placeholder() {
      return this.type === 'case' ? '请输入病例名进行搜索' : '请输入文献名进行搜索'
    },
    emptyDescription() {
      return this.type === 'case' ? '请输入关键词搜索病例' : '请输入关键词搜索文献'
    },
    nameProp() {
      return 'liteName'
    },
    nameLabel() {
      return this.type === 'case' ? '病例名称' : '文献名称'
    }
  },
  data() {
    return {
      dialogShow: false,
      searchKey: '',
      literatureList: [],
      departmentTree: [],
      selectedDepartmentIds: [],
      searchTimer: null,
      tableLoading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      minSearchLength: 2
    }
  },
  watch: {
    show: {
      handler(val) {
        this.dialogShow = val
        if (val) {
          this.currentPage = 1
          this.literatureList = []
          this.total = 0
          this.selectedDepartmentIds = []
          this.loadDepartmentList()
        }
      },
      immediate: true
    },

  },
  methods: {
    handleDialogClose() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.$emit('update-show', false)
      this.searchKey = ''
      this.literatureList = []
      this.total = 0
      this.currentPage = 1
      this.selectedDepartmentIds = []
    },

    async loadDepartmentList() {
      try {
        const res = await getDepartmentList(this.type)
        if (res.code === 200 && res.data) {
          this.departmentTree = this.convertToTreeFormat(res.data)
        }
      } catch (e) {
        console.error('加载分类列表失败:', e)
        this.departmentTree = []
      }
    },

    convertToTreeFormat(data) {
      return data.map(item => ({
        value: item.id,
        label: item.name,
        children: item.children && item.children.length > 0
          ? this.convertToTreeFormat(item.children)
          : []
      }))
    },

    onInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }

      if (!this.searchKey?.trim() && this.selectedDepartmentIds.length === 0) {
        this.literatureList = []
        this.total = 0
        return
      }

      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.handleSearch()
      }, 300)
    },

    onCategoryChange() {
      this.currentPage = 1
      // 如果没有搜索关键词但选择了分类，也进行搜索
      if (this.selectedDepartmentIds.length > 0) {
        this.handleSearch()
      } else if (!this.searchKey?.trim()) {
        this.literatureList = []
        this.total = 0
      }
    },

    async handleSearch() {
      this.tableLoading = true
      try {
        const departmentId = this.selectedDepartmentIds.length > 0
          ? this.selectedDepartmentIds[this.selectedDepartmentIds.length - 1]
          : null

        const res = await getLiterature({
          liteName: this.searchKey.trim(),
          departmentId: departmentId,
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          type: this.type
        })

        if (res.code === 200 && res.data) {
          if (res.data.list !== undefined) {
            this.literatureList = res.data.list || []
            this.total = res.data.total || 0
          } else {
            this.literatureList = res.data || []
            this.total = this.literatureList.length
          }
        } else {
          this.literatureList = []
          this.total = 0
        }
      } catch (e) {
        console.error('搜索失败:', e)
        this.literatureList = []
        this.total = 0
      } finally {
        this.tableLoading = false
      }
    },

    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.handleSearch()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.handleSearch()
    },

    handleSelect(row) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.$emit('select', row)
      this.dialogShow = false
      this.$emit('update-show', false)
    },

    formatDate(dateStr) {
      if (!dateStr) return '未知时间'
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '未知时间'
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    }
  }
}
</script>

<style lang="scss" scoped>
/* 弹窗整体样式 */
.literature-search-dialog {
  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px 24px;
    border-radius: 12px 12px 0 0;

    .el-dialog__title {
      color: #fff;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__close {
      color: rgba(255, 255, 255, 0.8);
      font-size: 20px;

      &:hover {
        color: #fff;
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 24px;
    background: #f8f9fa;
    max-height: 60vh;
    overflow-y: auto;
  }

  ::v-deep .el-dialog__footer {
    padding: 16px 24px;
    background: #fff;
    border-radius: 0 0 12px 12px;
    border-top: 1px solid #eee;
  }
}

/* 搜索容器 */
.search-container {
  margin-bottom: 24px;
}

/* 分类筛选 */
.category-filter {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.filter-label {
  font-size: 14px;
  color: #666;
  margin-right: 12px;
}

.category-cascader {
  width: 320px;

  ::v-deep .el-input__wrapper {
    border-radius: 6px;
    height: 36px;
    border: 1px solid #e4e7ed;
    transition: all 0.3s ease;

    &:hover {
      border-color: #667eea;
    }

    &.is-focus {
      border-color: #667eea;
      box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
    }
  }

  ::v-deep .el-input__inner {
    height: 34px;
    line-height: 34px;
    font-size: 14px;
  }

  ::v-deep .el-cascader-menu {
    min-width: 160px;
    max-height: 300px;
    overflow-y: auto;
  }

  ::v-deep .el-cascader-panel {
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    border: none;
  }

  ::v-deep .el-cascader-node {
    padding: 10px 12px;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f5f7fa;
      color: #667eea;
    }

    &.is-active {
      color: #667eea;
      font-weight: 500;
    }

    &.is-selected {
      color: #667eea;
      font-weight: 600;
    }
  }

  ::v-deep .el-cascader-node__label {
    display: flex;
    align-items: center;
  }

  ::v-deep .el-cascader-node__expand-icon {
    font-size: 14px;
    color: #999;
    margin-right: 6px;

    &:hover {
      color: #667eea;
    }
  }
}

.search-box {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 16px;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding-left: 44px;

  ::v-deep .el-input__wrapper {
    border-radius: 10px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: none;
    height: 48px;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    }

    &.is-focus {
      box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
    }
  }
}

.search-result-count {
  margin-top: 12px;
  font-size: 14px;
  color: #666;
  text-align: right;

  .count-num {
    color: #667eea;
    font-weight: 600;
    font-size: 16px;
  }
}

/* 内容容器 */
.content-container {
  min-height: 300px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;

  .empty-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e8f4fd 0%, #f3e8ff 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    i {
      font-size: 40px;
      color: #667eea;
    }

    &.no-result {
      background: linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%);

      i {
        color: #f59e0b;
      }
    }
  }

  .empty-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
  }

  .empty-desc {
    font-size: 14px;
    color: #999;
    margin: 0;
  }
}

/* 结果列表 */
.result-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.result-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-left: 3px solid #667eea;
  }

  .item-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    flex-shrink: 0;

    i {
      color: #fff;
      font-size: 20px;
    }
  }

  .item-content {
    flex: 1;
    overflow: hidden;

    .item-title {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      margin: 0 0 8px 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .item-meta {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: #999;
      margin: 0;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;

        i {
          font-size: 12px;
        }
      }

      .meta-divider {
        margin: 0 8px;
        color: #ddd;
      }
    }
  }

  .item-action {
    margin-left: 16px;

    .select-btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 6px;
      padding: 6px 16px;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

/* 加载状态 */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

/* 分页区域 */
.pagination-footer {
  display: flex;
  justify-content: center;
}

.footer-actions {
  display: flex;
  justify-content: center;

  .cancel-btn {
    padding: 8px 24px;
    border-radius: 6px;
  }
}
</style>
